// Credenciamento.tsx
import {
  Box,
  Button,
  Divider,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  useToast
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import { handleLogout } from '../components/handleLogout'
import api from '../utils/api'
import CadastroUsuario from './credenciamento/CadastroUsuario'
import ConsultaPanel from './credenciamento/ConsultaPanel'
import ImpressaoLote from './credenciamento/ImpressaoLote'
import ResumoPanel from './credenciamento/ResumoPanel'
import { Pessoa } from './credenciamento/types'

// Identificadores únicos para os toasts
const TOAST_ID_NOT_FOUND = "toast-not-found";
const TOAST_ID_ERROR = "toast-error";

const Credenciamento: React.FC = () => {
  const toast = useToast()
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('Nome')
  const [searchValue, setSearchValue] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [usuarios, setUsuarios] = useState<Pessoa[]>([])
  const [pessoaEdit, setPessoaEdit] = useState<Pessoa | null>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0
  })
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    totalCheckins: 0,
    // Pessoas credenciadas HOJE. A API ja devolvia e a tela jogava fora: num evento de
    // tres dias, "Total de Check-in" soma os dias e ninguem sabe quantos passaram hoje.
    totalPessoasCheckinHoje: 0,
    totalAccesses: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  /*
    Descarta resposta atrasada.

    Cada busca leva um número; ao voltar, a resposta só é aplicada se ainda for a última
    pedida. Sem isto, digitar depressa dispara vários pedidos e quem manda na tela é o que
    responder por último — que costuma ser o mais antigo, porque o termo curto casa com
    mais gente e demora mais. Era assim que o resultado anterior reaparecia sobre o novo.
  */
  const buscaAtual = useRef(0)

  /*
   * Última combinação já buscada (termo|página|tamanho).
   *
   * Quem clica em Buscar chama a função direto com o termo e também atualiza o estado; o
   * efeito abaixo reage a essa atualização e pediria a MESMA busca de novo. O botão
   * continua podendo repetir a busca de propósito — a dedução vale só para o efeito.
   */
  const ultimaChave = useRef('')

  // 🔍 Função para buscar usuários na API
  const fetchUsuarios = async (termoParam?: string) => {
    // O termo chega por parâmetro quando quem pede acabou de digitá-lo: o estado só
    // acompanha no próximo render, e ler dele aqui buscaria o termo anterior.
    const termo = (termoParam ?? searchValue).trim()

    // Se o campo de busca estiver vazio ou com menos de 3 caracteres, reseta a página
    if (!termo || termo.length < 3) {
      buscaAtual.current++; // invalida resposta em voo: a lista foi limpa de propósito
      ultimaChave.current = `${termo}|1|${pageSize}`;
      setPage(1); // Reseta para a primeira página
      setUsuarios([]);
      setPaginationInfo({ page: 1, limit: pageSize, total: 0, totalPages: 0 });

      // 🔹 Remove qualquer toast ativo quando a busca for limpa
      if (toast.isActive(TOAST_ID_NOT_FOUND)) toast.close(TOAST_ID_NOT_FOUND);
      if (toast.isActive(TOAST_ID_ERROR)) toast.close(TOAST_ID_ERROR);

      return;
    }

    // Termo novo recomeça da primeira página: a página 3 do resultado anterior não tem
    // relação nenhuma com esta busca, e pedi-la devolve lista vazia.
    const paginaPedida = termoParam !== undefined && termoParam.trim() !== searchValue.trim() ? 1 : page

    // Busca unificada — backend detecta automaticamente o tipo
    const params: any = { page: paginaPedida, limit: pageSize, search: termo };

    const meuNumero = ++buscaAtual.current
    ultimaChave.current = `${termo}|${paginaPedida}|${pageSize}`

    // Busca os usuários na API
    setIsLoading(true);
    try {
      const response = await api.get(`/users/searchpagination`, { params });
      if (meuNumero !== buscaAtual.current) return; // chegou tarde: já há busca mais nova
      const { users, total, totalPages } = response.data;
      const usuariosFormatados: Pessoa[] = users.map((user: any) => ({
        id: user.uid,
        nome: user.name,
        email: user.email,
        cpf: user.cpfCnpj || user.cpf || '',
        telefone: '',       // se disponível, mapeie aqui
        inscricao: '',      // ajuste conforme necessário
        categoria: '',      // ajuste conforme necessário
        checkin: '',        // ajuste conforme necessário
        customFields: user.customFields,
        registrationUid: user.registrationUid,
        checkinCount: user.checkinCount || 0,
        ultimoCheckin: user.ultimoCheckin || null
      }));
      setUsuarios(usuariosFormatados);
      setPaginationInfo({ page: response.data.page, limit: response.data.limit, total, totalPages });
      // 🔹 Se a busca retornar resultados, fecha os toasts de erro/not found
      if (toast.isActive(TOAST_ID_NOT_FOUND)) toast.close(TOAST_ID_NOT_FOUND);
      if (toast.isActive(TOAST_ID_ERROR)) toast.close(TOAST_ID_ERROR);
    } catch (error: any) {
      // Erro atrasado também não manda na tela: um 404 de termo antigo apagaria o
      // resultado certo que já chegou.
      if (meuNumero !== buscaAtual.current) return;
      if (error.response?.status === 404) {
        // Evita exibir múltiplos toasts de "Nenhum usuário encontrado"
        if (!toast.isActive(TOAST_ID_NOT_FOUND)) {
          toast({
            id: TOAST_ID_NOT_FOUND,
            title: "Nenhum usuário encontrado.",
            status: "info",
            duration: 3000,
            isClosable: true
          });
        }
        setUsuarios([]);
        setPaginationInfo({ page: 1, limit: pageSize, total: 0, totalPages: 0 });
      } else {
        // Evita exibir múltiplos toasts de erro
        if (!toast.isActive(TOAST_ID_ERROR)) {
          toast({
            id: TOAST_ID_ERROR,
            title: "Erro ao buscar usuários.",
            description: error.response?.data?.message || "Erro desconhecido.",
            status: "error",
            duration: 3000,
            isClosable: true
          });
        }
      }
    } finally {
      // Só a busca mais recente apaga o indicador: senão uma resposta velha esconde que
      // ainda há pedido em andamento.
      if (meuNumero === buscaAtual.current) setIsLoading(false);
    }
  }

  /*
   * Um efeito só decide a página e dispara a busca.
   *
   * Eram dois — um buscava, outro voltava para a página 1 ao trocar o termo. Como o de
   * busca roda antes, ele chegava a pedir a página ANTIGA com o termo NOVO (a página 3 de
   * uma busca que tem uma página), e essa resposta vazia era a mais recente: apagava o
   * resultado certo. Juntos, a página é acertada antes de qualquer pedido sair.
   */
  const termoAnterior = useRef(searchValue)

  useEffect(() => {
    const termo = searchValue.trim()

    if (termoAnterior.current.trim() !== termo) {
      termoAnterior.current = searchValue
      // Trocar a página re-executa este efeito; a busca sai na segunda passagem.
      if (page !== 1) {
        setPage(1)
        return
      }
    }

    const chave = `${termo}|${page}|${pageSize}`
    if (chave === ultimaChave.current) return
    fetchUsuarios()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, page, pageSize])

  const handleEdit = (pessoa: Pessoa) => {
    setPessoaEdit(pessoa)
    setTabIndex(1)
  }

  const handleNew = () => {
    setPessoaEdit({
      id: undefined,
      nome: '',
      email: '',
      cpf: '',
      telefone: '',
      inscricao: '',
      categoria: '',
      checkin: '',
      customFields: { authorization: false },
      registrationUid: undefined
    })
    setTabIndex(1)
  }

  const handleSave = async (pessoa: Pessoa) => {
    // Validação dos campos obrigatórios
    if (!pessoa.nome || !pessoa.cpf) {
      toast({
        title: "Preencha todos os campos obrigatórios (Nome e CPF/Documento Único).",
        status: "warning",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // Converte os campos para o padrão do back-end
    const payload = {
      name: pessoa.nome,
      email: pessoa.email,
      cpfCnpj: pessoa.cpf,
      phone: pessoa.telefone || pessoa.customFields?.celular || undefined,
      customFields: pessoa.customFields,
      registrationUid: pessoa.registrationUid,
    };

    try {
      let savedUser: Pessoa;
      if (pessoa.id) {
        // Atualiza usuário existente (usa o uid no endpoint)
        const response = await api.patch(`/users/${pessoa.id}`, payload)

        savedUser = { ...pessoa, ...response.data };

        // Limpa o campo de pesquisa e atualiza os dados
        setSearchValue("");

        // Atualiza as estatísticas
        fetchRegistrationStats();

        toast({
          title: "Usuário atualizado com sucesso.",
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      } else {
        // Cria um novo usuário
        const response = await api.post('/users', payload)

        savedUser = { ...pessoa, ...response.data };

        // Limpa o campo de pesquisa e atualiza os dados
        setSearchValue("");

        // Limpa o campo de pesquisa e atualiza os dados
        fetchRegistrationStats();

        toast({
          title: "Usuário cadastrado com sucesso.",
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      }
      setPessoaEdit(null)
      setTabIndex(0)
      fetchUsuarios() // Atualiza a listagem
      return savedUser; // 🔥 Retorna o usuário salvo
    } catch (error: any) {
      // 🚨 Captura erros específicos e mostra mensagens detalhadas
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || "Erro desconhecido.";

      let toastTitle = "Erro ao salvar usuário.";
      let toastStatus: "error" | "warning" = "error";

      if (statusCode === 409) {
        toastTitle = "CPF/CNPJ já cadastrado!";
        toastStatus = "warning";
      } else if (statusCode === 400) {
        toastTitle = "Erro de validação.";
      } else if (statusCode === 500) {
        toastTitle = "Erro interno do servidor.";
      }

      toast({
        title: toastTitle,
        description: errorMessage,
        status: toastStatus,
        duration: 5000,
        isClosable: true,
      });
    }
  }

  // Função para buscar as estatísticas:
  const fetchRegistrationStats = async () => {
    try {
      const response = await api.get('/registration/stats');
      setStats(response.data);
    } catch (error: any) {
      console.error("Erro ao buscar estatísticas:", error.response?.data || error);
    }
  };

  // Atualiza a cada 1 minuto:
  useEffect(() => {
    fetchRegistrationStats();
    const interval = setInterval(fetchRegistrationStats, 180000);
    return () => clearInterval(interval);
  }, []);

  return (<>
    <Box
      w="100%"
      minH="100vh"
      bgGradient={useColorModeValue("linear(to-r, gray.50, white)", "linear(to-r, gray.800, gray.700)")}
      py={{ base: 4, md: 8 }}
      px={{ base: 2, md: 10 }}
    >
      <Box
        maxW={{ base: "100%", md: "1200px" }}
        mx="auto"
        p={{ base: 4, md: 6 }}
        bg={useColorModeValue("white", "gray.800")}
        borderRadius="lg"
        boxShadow={{ base: "md", md: "xl" }}
      >
        <Heading
          as="h1"
          fontSize={{ base: 'xl', md: '3xl' }}
          fontWeight="bold"
          textAlign="left"
          color={useColorModeValue('blue.600', 'blue.300')}
          mb={4}
        >
          Credenciamento
        </Heading>

        <Divider borderColor={useColorModeValue('gray.300', 'gray.600')} mb={{ base: 4, md: 6 }} />

        <Tabs
          index={tabIndex}
          onChange={setTabIndex}
          variant="enclosed"
          colorScheme="blue"
          isFitted /* Tabs ocupam largura total no mobile */
        >
          <TabList
            mb={{ base: 2, md: 4 }}
            bg={useColorModeValue('gray.100', 'gray.700')}
            p={{ base: 1, md: 2 }}
            borderRadius="md"
            w="100%"
            overflowX="auto" /* permite rolagem horizontal no mobile */
          >
            <Tab
              _selected={{ bg: 'blue.500', color: 'white', borderRadius: 'md' }}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Consulta
            </Tab>
            <Tab
              _selected={{ bg: 'blue.500', color: 'white', borderRadius: 'md' }}
              onClick={() => handleNew()}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Cadastro
            </Tab>
            <Tab
              _selected={{ bg: 'blue.500', color: 'white', borderRadius: 'md' }}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Impressão em Lote
            </Tab>
            {/*
              A pergunta da organizacao chega ao BALCAO, nao ao admin: alguem passa e
              pergunta quanta gente entrou hoje. Ter o numero aqui evita abrir outra
              ferramenta no meio da fila.
            */}
            <Tab
              _selected={{ bg: 'blue.500', color: 'white', borderRadius: 'md' }}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Resumo
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <Button
                colorScheme="blue"
                onClick={handleNew}
                mb={{ base: 2, md: 4 }}
                boxShadow="md"
                w={{ base: 'full', md: 'auto' }} // no mobile ocupa toda a largura
              >
                Novo Cadastro
              </Button>
              <ConsultaPanel
                searchType={searchType}
                setSearchType={setSearchType}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearch={fetchUsuarios}
                totalRegistrations={stats.totalRegistrations}
                totalCheckins={stats.totalCheckins}
                totalPessoasCheckinHoje={stats.totalPessoasCheckinHoje}
                totalAccesses={stats.totalAccesses}
                pageSize={pageSize}
                setPageSize={setPageSize}
                page={page}
                totalPages={paginationInfo.totalPages}
                handlePrevPage={() => setPage(page - 1)}
                handleNextPage={() => setPage(page + 1)}
                allData={usuarios}
                handleEdit={handleEdit}
                fetchRegistrationStats={fetchRegistrationStats}
                isLoading={isLoading}
              />
            </TabPanel>

            <TabPanel px={0}>
              <CadastroUsuario
                pessoaEdit={pessoaEdit ?? undefined}
                onSave={handleSave}
                onCancel={() => setTabIndex(0)}
              />
            </TabPanel>

            <TabPanel px={0}>
              <ImpressaoLote />
            </TabPanel>

            <TabPanel px={0}>
              <ResumoPanel />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
    <Footer
      operatorName="Operador Credenciamento"
      onLogout={() => handleLogout()}
      onLeaveRoom={() => navigate('/access')}
      onSettings={() => console.log('Abrir configurações')}
      onToggleFullscreen={() => console.log('Alternar fullscreen')}
    />
  </>)
}

export default Credenciamento
